/**
 * The data layer for the Opposition screen — `server/app/routers/opposition.py`
 * plus the two `/api/matters` calls that actually open one.
 *
 * Its own module rather than more of `pipeline.ts` for the reason that file
 * gives for keeping matters *in* it: a module holds one feature area, and this
 * is a different one. Opposition has a second party, a hard gate before
 * creation, a deadline series rather than a date, and two deployment flags
 * that decide whether any of it is reachable — none of which the three P2
 * control screens know or should learn.
 *
 * Three rules this module keeps, each because breaking it has a specific
 * failure:
 *
 * - **The conflict verdict is the server's word.** {@link ConflictCheckResult}
 *   carries `clears` and it is never recomputed here from `severity` or from
 *   `hits`. A screen that decided for itself when a check passed would be a
 *   second gate, and the second gate is always the lenient one — the same rule
 *   `pipeline.ts` keeps for `exportable`.
 * - **A missing rule is not a missing deadline.** {@link OppositionCascade}
 *   carries `unchecked_reason` beside an empty `steps`, and the screen renders
 *   the reason. An empty list on its own reads as "this opposition has no
 *   deadlines", which is the opposite of what it means.
 * - **The flags come from the server.** Nothing here hard-codes whether
 *   opposition or drafting is on. A page that assumes is a page whose last
 *   button answers 501.
 */

import {
  useMutation,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query'

import { api } from './client'
import type { Jurisdiction, Matter } from './types'

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/**
 * `server.app.deadlines.rules.DeadlineStep`. A proceeding's deadlines are a
 * series, and the step says which one a date is — reconciling a printed
 * evidence date against a computed counterstatement date would produce a
 * confident mismatch about two different deadlines.
 */
export type DeadlineStep =
  | 'RESPONSE'
  | 'COUNTERSTATEMENT'
  | 'OPPONENT_EVIDENCE'

/** How each step is named to a person. The enum value is a schema token. */
const STEP_LABELS: Record<DeadlineStep, string> = {
  RESPONSE: 'Response',
  COUNTERSTATEMENT: 'Counterstatement',
  OPPONENT_EVIDENCE: "Opponent's evidence",
}

export function stepLabel(step: string): string {
  return STEP_LABELS[step as DeadlineStep] ?? step
}

/** `server.app.services.conflicts.ConflictSeverity`. */
export type ConflictSeverity = 'CLEAR' | 'REVIEW' | 'CONFLICT'

// ---------------------------------------------------------------------------
// Readiness
// ---------------------------------------------------------------------------

export interface OfficeReadiness {
  office: Jurisdiction
  has_counterstatement_rule: boolean
  rule_id: string | null
  /** `PROVISIONAL` or `CONFIRMED`. Every opposition rule is provisional today. */
  rule_status: string | null
  /** What the office does when the period is missed, in its own terms. */
  consequence: string | null
  cascade_steps: number
}

export interface OppositionReadiness {
  opposition_enabled: boolean
  drafting_enabled: boolean
  /** Why drafting is off, in the server's words. Never invented here. */
  drafting_disabled_reason: string | null
  offices: OfficeReadiness[]
}

export const oppositionKeys = {
  readiness: ['opposition', 'readiness'] as const,
  cascade: (office: string, notice: string) =>
    ['opposition', 'cascade', office, notice] as const,
}

export function useOppositionReadiness(): UseQueryResult<OppositionReadiness> {
  return useQuery<OppositionReadiness>({
    queryKey: oppositionKeys.readiness,
    queryFn: () => api.get<OppositionReadiness>('/api/opposition/readiness'),
  })
}

// ---------------------------------------------------------------------------
// The conflict check
// ---------------------------------------------------------------------------

export interface ConflictHit {
  matched_name: string
  /** How we know them: `applicant on GW-2026-0001`, `instructing agent`. */
  relationship: string
  matter_id: string | null
  /** An exact match. Never waivable — the server refuses it outright. */
  exact: boolean
}

export interface ConflictCheckResult {
  proposed: string
  normalised: string
  severity: ConflictSeverity
  /** The server's verdict. Never recomputed here. */
  clears: boolean
  hits: ConflictHit[]
  waived: string[]
  /** One line a person can act on, worded by the server. */
  summary: string
}

export interface ConflictCheckRequest {
  opponent_name: string
  conflict_waiver?: string[]
}

/**
 * Ask what creation would say, without creating anything.
 *
 * A mutation rather than a query even though it writes nothing: it is an act a
 * person takes at a moment they choose, and caching it by opponent name would
 * hand back a verdict computed against a database that has since changed. The
 * check runs again at creation and *that* run is the one that decides.
 */
export function useConflictCheck() {
  return useMutation<ConflictCheckResult, unknown, ConflictCheckRequest>({
    mutationFn: (body) =>
      api.post<ConflictCheckResult>('/api/opposition/conflict-check', body),
  })
}

// ---------------------------------------------------------------------------
// The deadline cascade
// ---------------------------------------------------------------------------

export interface CascadeStep {
  step: DeadlineStep
  rule_id: string
  due_date: string
  /** What this step's period was counted from — the notice, or an earlier due date. */
  counted_from: string
  counted_from_step: DeadlineStep | null
  /** Does missing this one end the matter? Drives hard-stop vs date-to-watch. */
  fatal: boolean
  rule_status: string
  authority: string
  consequence: string
  runs_from: string
  open_questions: string[]
}

export interface OppositionCascade {
  office: Jurisdiction
  proceeding_type: string
  notice_date: string
  first_step: DeadlineStep
  steps: CascadeStep[]
  /** Present when `steps` is empty. Rendered — see this module's docstring. */
  unchecked_reason: string | null
}

/**
 * The whole series for one office, computed from the notice date.
 *
 * `enabled` is false until there is a date, so an empty form does not fire a
 * request whose answer would be a validation error rather than an answer.
 */
export function useOppositionCascade(
  office: Jurisdiction | '',
  noticeDate: string,
): UseQueryResult<OppositionCascade> {
  return useQuery<OppositionCascade>({
    queryKey: oppositionKeys.cascade(office, noticeDate),
    queryFn: () =>
      api.get<OppositionCascade>('/api/opposition/cascade', {
        query: { office, notice_date: noticeDate },
      }),
    enabled: Boolean(office) && Boolean(noticeDate),
  })
}

// ---------------------------------------------------------------------------
// Opening one
// ---------------------------------------------------------------------------

/**
 * `MatterCreateRequest` with the three opposition fields.
 *
 * A separate interface rather than optional fields on the shared one: an
 * examination create that carried `opponent_name` is refused by the server
 * (422 `opponent_on_examination_matter`), so the two request shapes are
 * genuinely different and a single type with everything optional would let
 * that mistake typecheck.
 */
export interface OpenOppositionRequest {
  jurisdiction: Jurisdiction
  proceeding_type: 'OPPOSITION'
  opponent_name: string
  opponent_counsel?: string | null
  applicant_name?: string | null
  application_number?: string | null
  mark_text?: string | null
  deadline_date?: string | null
  /** Names the analyst has looked at and confirmed are a different party. */
  conflict_waiver?: string[]
}

export function useOpenOpposition() {
  return useMutation<Matter, unknown, OpenOppositionRequest>({
    mutationFn: (body) => api.post<Matter>('/api/matters', body),
  })
}
